import React, { useState, useEffect, useCallback } from 'react';
import './PortfolioDetail.css';

interface PortfolioItem {
  id: number;
  title: string;
  category: string;
  location: string;
  image: string;
  images?: string[];
  description: string;
  details: {
    year: string;
    area: string;
    client: string;
  };
  size: string;
  offsetY: string;
}

interface PortfolioDetailProps {
  item: PortfolioItem | null;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (direction: 'prev' | 'next') => void;
}

const PortfolioDetail: React.FC<PortfolioDetailProps> = ({
  item,
  isOpen,
  onClose,
  onNavigate
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const images = item?.images || [item?.image || ''];

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [item]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          handleClose();
          break;
        case 'ArrowLeft':
          handlePrevImage();
          break;
        case 'ArrowRight':
          handleNextImage();
          break;
        case 'ArrowUp':
          e.preventDefault();
          onNavigate('prev');
          break;
        case 'ArrowDown':
          e.preventDefault();
          onNavigate('next');
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onNavigate]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  }, [onClose]);

  const handlePrevImage = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentImageIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleNextImage = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentImageIndex((prev) =>
      prev === images.length - 1 ? 0 : prev + 1
    );
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handlePrevProject = () => {
    onNavigate('prev');
  };

  const handleNextProject = () => {
    onNavigate('next');
  };

  // Touch handlers for swipe functionality
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && images.length > 1) {
      handleNextImage(); // Swipe left = next image
    }
    if (isRightSwipe && images.length > 1) {
      handlePrevImage(); // Swipe right = previous image
    }
  };

  if (!item || !isOpen) return null;

  return (
    <div className={`portfolio-detail-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className="portfolio-detail-container" onClick={(e) => e.stopPropagation()}>

        {/* Project Navigation - Outside Modal */}
        <button className="portfolio-nav-btn prev-project" onClick={handlePrevProject}>
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="portfolio-detail-modal">
          {/* Close Button */}
          <button className="portfolio-close-btn" onClick={handleClose}>
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

        <div className="portfolio-detail-content">

          {/* Image Section - 67% */}
          <div className="portfolio-image-section">
            <div
              className="image-container"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div
                className="image-carousel"
                style={{
                  '--image-count': images.length,
                  width: `${images.length * 100}%`,
                  transform: `translateX(-${currentImageIndex * (100 / images.length)}%)`,
                  transition: isTransitioning ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
                } as React.CSSProperties}
              >
                {images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${item.title} - Image ${index + 1}`}
                    className="main-image"
                  />
                ))}
              </div>

              {/* Image Navigation */}
              {images.length > 1 && (
                <>
                  <button className="image-nav-btn prev" onClick={handlePrevImage}>
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>

                  <button className="image-nav-btn next" onClick={handleNextImage}>
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>

                  {/* Image Indicators */}
                  <div className="image-indicators">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={() => {
                          if (isTransitioning || index === currentImageIndex) return;
                          setIsTransitioning(true);
                          setCurrentImageIndex(index);
                          setTimeout(() => setIsTransitioning(false), 300);
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Details Section - 33% */}
          <div className="portfolio-details-section">
            <div className="details-content">

              {/* Project Info */}
              <div className="project-header">
                <div className="project-category">
                  {item.category.toUpperCase()} / {item.location.toUpperCase()}
                </div>
                <h1 className="project-title">{item.title}</h1>
              </div>

              {/* Project Description */}
              <div className="project-description">
                <p>{item.description}</p>
              </div>

              {/* Project Details */}
              <div className="project-details">
                <div className="detail-item">
                  <span className="detail-label">Year</span>
                  <span className="detail-value">{item.details.year}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Area</span>
                  <span className="detail-value">{item.details.area}</span>
                </div>
                {/* <div className="detail-item">
                  <span className="detail-label">Client</span>
                  <span className="detail-value">{item.details.client}</span>
                </div> */}
              </div>

              {/* Contact CTA */}
              <div className="project-cta">
                <a href="/contact" className="cta-button">
                  <span>Discuss Your Project</span>
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>

            </div>
          </div>

        </div>
        </div>

        <button className="portfolio-nav-btn next-project" onClick={handleNextProject}>
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

      </div>
    </div>
  );
};

export default PortfolioDetail;